import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { ProjectStatus, ProjectHealth } from './projectsApi';

// --- KB project edit + overview (CLE-54) -----------------------------------
//
// Projects are markdown-first (CLE-37): the README.md is the source of truth and
// the DB is derived from it. Most mutations go through the AI assistant, but a
// curated set of project fields can be edited directly via this client, which
// drives the backend `/api/kb/projects/:code` routes (markdown-first edit + a
// KB-aggregated overview).

export type BudgetCurrency = 'RON' | 'EUR';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface Envelope<T> {
  success: true;
  data: T;
}

// --- Edit ------------------------------------------------------------------

// Only the provided fields are written; `null` clears a value, `undefined`
// leaves it untouched. Dates are ISO `YYYY-MM-DD` strings (or null to clear).
// `clientId` links a known Client (null clears): the backend resolves it to the
// client's name for the markdown AND persists the DB-only association.
export interface UpdateKbProjectInput {
  /**
   * Renaming rewrites the README title + frontmatter and re-derives the DB row.
   * The KB folder never moves: a project without a `code` gets one pinned to its
   * current slug so every path stays valid (the backend returns it).
   */
  name?: string;
  status?: ProjectStatus;
  health?: ProjectHealth;
  clientId?: string | null;
  budget?: number | null;
  budgetCurrency?: BudgetCurrency;
  startDate?: string | null;
  endDate?: string | null;
  description?: string | null;
}

export interface UpdatedKbProject {
  id: string;
  code: string | null;
  name: string;
  status: ProjectStatus;
  health: ProjectHealth;
  client: string | null;
  budget: number | null;
  budgetCurrency: BudgetCurrency;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  markdownPath: string;
  sync: string;
}

export async function updateKbProject(
  codeOrId: string,
  input: UpdateKbProjectInput,
): Promise<UpdatedKbProject> {
  const res = await apiFetch<Envelope<UpdatedKbProject>>(
    `/api/kb/projects/${encodeURIComponent(codeOrId)}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data;
}

// --- Create (CLE-223) ------------------------------------------------------
//
// Manual project creation stays markdown-first (CLE-41): the backend writes a
// README.md and then syncs the DB, so this never violates the "DB is derived"
// invariant. `client` is a free-text name; pass `clientId` to resolve it from a
// known Client (the backend looks up the name). Dates are ISO `YYYY-MM-DD`.
export interface CreateKbProjectInput {
  name: string;
  code?: string;
  description?: string | null;
  status?: ProjectStatus;
  health?: ProjectHealth;
  client?: string | null;
  clientId?: string | null;
  budget?: number | null;
  budgetCurrency?: BudgetCurrency;
  isBarter?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  /**
   * Optional task template (CLE-DASH): after the README sync creates the DB
   * project, the backend instantiates the template's tasks in it.
   */
  taskTemplateId?: string | null;
}

export interface CreatedKbProject {
  id: string | null;
  code: string | null;
  name: string;
  status: ProjectStatus;
  health: ProjectHealth;
  client: string | null;
  budget: number | null;
  budgetCurrency: BudgetCurrency;
  isBarter: boolean;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  markdownPath: string;
  sync: string;
}

export async function createKbProject(input: CreateKbProjectInput): Promise<CreatedKbProject> {
  const res = await apiFetch<Envelope<CreatedKbProject>>('/api/kb/projects', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// --- Team members (manual, markdown-first) ---------------------------------
//
// Adding writes the `## Echipa` row via allocatePersonMarkdownFirst and then
// re-derives the DB rows; removing rewrites the table AND explicitly deletes
// the derived project_members/allocations rows (the sync engine only upserts).

export type ProjectMemberRole = 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'STAKEHOLDER';

export interface AddKbProjectMemberInput {
  personId: string;
  role?: ProjectMemberRole;
  /** Weekly hours the person spends on this project (CLE-444). */
  allocationHours?: number;
  /** ISO `YYYY-MM-DD` (or null/omitted). */
  startDate?: string | null;
  endDate?: string | null;
}

export interface AddedKbProjectMember {
  member: {
    email: string;
    role: ProjectMemberRole;
    allocationHours: number;
    startDate: string | null;
    endDate: string | null;
    personId: string;
    fullName: string;
  };
  /** 'ok' | 'failed' — markdown-first: a failed DB sync is a warning, not an error. */
  sync: string;
  syncReason?: string;
}

export async function addKbProjectMember(
  codeOrId: string,
  input: AddKbProjectMemberInput,
): Promise<AddedKbProjectMember> {
  const res = await apiFetch<Envelope<AddedKbProjectMember>>(
    `/api/kb/projects/${encodeURIComponent(codeOrId)}/members`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data;
}

export interface RemovedKbProjectMember {
  email: string;
  deleted: true;
  removed: { projectMembers: number; allocations: number };
}

export async function removeKbProjectMember(
  codeOrId: string,
  email: string,
): Promise<RemovedKbProjectMember> {
  const res = await apiFetch<Envelope<RemovedKbProjectMember>>(
    `/api/kb/projects/${encodeURIComponent(codeOrId)}/members/${encodeURIComponent(email)}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  );
  return res.data;
}

// --- Overview --------------------------------------------------------------

export interface OverviewProject {
  id: string;
  code: string | null;
  name: string;
  status: ProjectStatus;
  health: ProjectHealth;
  client: string | null;
  isBarter: boolean;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  budget: number | null;
  /** Currency `budget` is expressed in; RON for projects never edited. */
  budgetCurrency: BudgetCurrency;
}

export interface OverviewMember {
  email: string;
  role: string;
  allocationHours: number | null;
  startDate: string | null;
  endDate: string | null;
}

export interface OverviewTeam {
  total: number;
  members: OverviewMember[];
}

export interface OverviewMonthly {
  month: string;
  income: number;
  outcome: number;
}

export interface OverviewFinancials {
  totalRevenue: number;
  totalCost: number;
  profit: number;
  unpaidIncome: number;
  payableOutcome: number;
  monthly: OverviewMonthly[];
}

export interface OverviewMilestone {
  name: string;
  status: string;
  deadline: string | null;
  progress: number;
  overdue: boolean;
}

export interface OverviewMilestones {
  completion: number;
  items: OverviewMilestone[];
}

export interface OverviewTasks {
  total: number;
  active: number;
  overdue: number;
  byStatus: Record<string, number>;
}

export interface OverviewRisk {
  name: string;
  status: string;
  impact: string;
  action: string;
  critical: boolean;
}

export interface OverviewLogEntry {
  timestamp: string;
  message: string;
}

export interface ProjectOverview {
  project: OverviewProject;
  team: OverviewTeam;
  // CLE-432: omitted by the backend for roles without projects.canViewFinancials.
  financials?: OverviewFinancials;
  milestones: OverviewMilestones;
  tasks: OverviewTasks;
  risks: OverviewRisk[];
  recentLog: OverviewLogEntry[];
}

export async function getProjectOverview(codeOrId: string): Promise<ProjectOverview> {
  const res = await apiFetch<Envelope<ProjectOverview>>(
    `/api/kb/projects/${encodeURIComponent(codeOrId)}/overview`,
    { headers: authHeaders() },
  );
  return res.data;
}
